import { useCallback, useEffect, useState } from 'react'

/**
 * Time/duration state for a single `<video>` element.
 *
 * Deliberately local to the player: `videoPlaybackStore` is not involved.
 * `useVideoVisibility` re-runs `syncObservedElements` on every `state.videos`
 * mutation, so routing 4 Hz `timeupdate` writes through the store would
 * re-sync the IntersectionObserver four times a second.
 */

/** Seekable = a finite, positive duration. NaN (no metadata yet) and Infinity (live HLS) are not. */
export function isSeekableDuration(duration: number): boolean {
  return Number.isFinite(duration) && duration > 0
}

export function clampSeekTime(seconds: number, duration: number): number {
  if (!isSeekableDuration(duration) || !Number.isFinite(seconds)) return 0
  return Math.min(Math.max(seconds, 0), duration)
}

/** Keyboard step: 5s, or a tenth of the clip for anything shorter than 50s. */
export function seekStepSeconds(duration: number): number {
  return Math.min(5, duration / 10)
}

/** Maps a 0..1 track fraction to seconds (clamped). */
export function fractionToTime(fraction: number, duration: number): number {
  return clampSeekTime(fraction * duration, duration)
}

/** `m:ss`, or `h:mm:ss` from one hour. Non-finite / negative → `0:00`. */
export function formatMediaTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  const paddedSeconds = String(secs).padStart(2, '0')
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`
  return `${minutes}:${paddedSeconds}`
}

export interface VideoProgress {
  /** Seconds. 0 until the element reports a position. */
  currentTime: number
  /** Seconds. NaN until metadata loads; Infinity for live streams. */
  duration: number
  /** Clamps to [0, duration] and writes `video.currentTime`. No-op when not seekable or no element. */
  seekTo: (seconds: number) => void
}

export function useVideoProgress(video: HTMLVideoElement | null): VideoProgress {
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState<number>(NaN)

  useEffect(() => {
    if (!video) {
      setCurrentTime(0)
      setDuration(NaN)
      return
    }

    // Metadata may already be loaded by the time this effect runs, in which
    // case no further event is coming: prime from the element.
    setDuration(video.duration)
    setCurrentTime(video.currentTime)

    const syncDuration = () => setDuration(video.duration)
    // `seeking` makes programmatic/keyboard seeks reflect immediately instead
    // of waiting up to 250ms for the next `timeupdate`.
    const syncTime = () => setCurrentTime(video.currentTime)
    // Fires on `video.load()` during the MP4 fallback and on any `src` swap.
    // The position is intentionally lost: the media changed.
    const handleEmptied = () => {
      setCurrentTime(0)
      setDuration(NaN)
    }

    video.addEventListener('loadedmetadata', syncDuration)
    video.addEventListener('durationchange', syncDuration)
    video.addEventListener('timeupdate', syncTime)
    video.addEventListener('seeking', syncTime)
    video.addEventListener('emptied', handleEmptied)

    return () => {
      video.removeEventListener('loadedmetadata', syncDuration)
      video.removeEventListener('durationchange', syncDuration)
      video.removeEventListener('timeupdate', syncTime)
      video.removeEventListener('seeking', syncTime)
      video.removeEventListener('emptied', handleEmptied)
    }
  }, [video])

  const seekTo = useCallback((seconds: number) => {
    // Duration is read from the element, not from state, so a seek issued in
    // the same tick as `loadedmetadata` still works.
    if (!video || !isSeekableDuration(video.duration)) return
    const next = clampSeekTime(seconds, video.duration)
    video.currentTime = next
    setCurrentTime(next)
  }, [video])

  return { currentTime, duration, seekTo }
}

/** Distance of the Back / Forward jump buttons and the j / l shortcuts. */
export const SEEK_JUMP_SECONDS = 10

/**
 * Seeks `video` by `delta` seconds, clamped to `[0, duration]`. Returns false
 * (and does nothing) without an element or before the element reports a
 * seekable duration, e.g. before metadata or on a live stream.
 */
export function jumpSeek(video: HTMLVideoElement | null, delta: number): boolean {
  if (!video || !isSeekableDuration(video.duration) || !Number.isFinite(delta)) return false
  video.currentTime = clampSeekTime(video.currentTime + delta, video.duration)
  return true
}

/**
 * True once `video` reports a finite, positive duration; false again after
 * `emptied` (a source swap). Listens to duration events only, so unlike
 * `useVideoProgress` it does not re-render on every `timeupdate`.
 */
export function useIsSeekable(video: HTMLVideoElement | null): boolean {
  const [seekable, setSeekable] = useState(false)

  useEffect(() => {
    if (!video) {
      setSeekable(false)
      return
    }
    const sync = () => setSeekable(isSeekableDuration(video.duration))
    const handleEmptied = () => setSeekable(false)
    sync()
    video.addEventListener('loadedmetadata', sync)
    video.addEventListener('durationchange', sync)
    video.addEventListener('emptied', handleEmptied)
    return () => {
      video.removeEventListener('loadedmetadata', sync)
      video.removeEventListener('durationchange', sync)
      video.removeEventListener('emptied', handleEmptied)
    }
  }, [video])

  return seekable
}
